import { updateDirector } from '@commenda/api';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  FieldWrapper,
  Icons,
  Label,
  LoadingIndicator,
  SingleFileUploader,
} from '@commenda/kakashi';
import { FileBasic } from '@commenda/types/src/file/types';
import { ServiceRequestDetails } from '@commenda/types/src/service-request/types';
import { LegacyDocumentType } from '@prisma/client';
import { Dispatch, SetStateAction, useState } from 'react';

type UploadFileModalProps = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  file: FileBasic;
  setFile: Dispatch<SetStateAction<FileBasic>>;
  documentType: LegacyDocumentType;
  heading?: string;
  subheading?: string;
  onUploadSuccess?: () => void;
  buttonText?: string;
  bannerComp?: React.ReactNode;
  issue_reported?: string;
  onViewUpload?: () => void;
  showCancelButton?: boolean;
  type?: 'corporation' | 'subsidiary';
  serviceRequestTaskId?: number;
  customComp?: React.ReactNode;
  directorId: number;
  serviceRequest: ServiceRequestDetails;
};

const DirectorUploadFileModal = ({
  isOpen,
  setIsOpen,
  file,
  setFile,
  documentType,
  subheading = '',
  heading = 'Upload Document',
  onUploadSuccess,
  buttonText = 'Upload',
  bannerComp,
  issue_reported,
  onViewUpload,
  showCancelButton = false,
  type = 'corporation',
  directorId,
  serviceRequestTaskId,
  customComp,
  serviceRequest,
}: UploadFileModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const upload = async () => {
    setIsSubmitting(true);

    const updateDirectorRes = await updateDirector(
      directorId,
      {
        corporationHasDirector: [
          {
            corporationId: serviceRequest?.corporation?.id,
            filesToAdd: [
              {
                fileName: file.fileName,
                fileUrl: file.fileUrl,
                documentType,
                serviceRequestTaskId,
              },
            ],
          },
        ],
      },
      undefined
    );

    if (!updateDirectorRes.ok) {
      setIsSubmitting(false);
      return;
    }

    if (onUploadSuccess) await onUploadSuccess();
    setIsSubmitting(false);
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="flex flex-col">
        <div className="flex flex-col gap-4 p-6">
          <div className="w-12 h-12 rounded-md fg-fg-secondary flex justify-center items-center bg-bg-primary border text-fg-secondary">
            <Icons.Upload01 className="text-fg-secondary" />
          </div>
          <div>
            <h3 className="text-lg text-text-primary-900">{heading}</h3>
            <p className="text-sm text-text-primary-900">{subheading}</p>
            {issue_reported && (
              <div className="mt-4">
                <Alert
                  title="Issue reported"
                  description={issue_reported}
                  variant="destructive"
                  icon={<Icons.AlertTriangle className="text-fg-error" />}
                />
              </div>
            )}
          </div>
          {bannerComp}

          <FieldWrapper>
            <Label>
              Upload document
              <span className="text-utility-violet-600 ml-1">*</span>
            </Label>
            <SingleFileUploader
              companyId={serviceRequest.companyId}
              file={
                file && !onViewUpload
                  ? {
                      fileName: file?.fileName,
                      fileUrl: file?.fileUrl,
                      documentType,
                    }
                  : undefined
              }
              accept={{
                'application/pdf': ['.pdf'],
                'image/*': ['.png', '.jpg', '.jpeg'],
              }}
              setFile={setFile}
              token=""
            />
          </FieldWrapper>
          {customComp}
        </div>
        <DialogFooter className="w-full">
          <div className="flex justify-between w-full items-center">
            <div>
              {onViewUpload && !showCancelButton && (
                <Button variant="link" size="md" onClick={onViewUpload}>
                  <span>View previous upload</span>
                  <Icons.ArrowUpRight className="size-4" />
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {showCancelButton && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-2"
                >
                  Cancel
                </Button>
              )}

              <Button
                onClick={upload}
                size="sm"
                disabled={isSubmitting || !file?.fileUrl}
                className="flex items-center gap-2"
              >
                {isSubmitting ? <LoadingIndicator /> : null} {buttonText}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DirectorUploadFileModal;
